import * as React from 'react'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { supabase } from './supabase'
import { Button } from './ui'

type VisitorReport = {
  today: string
  started_at: string
  checked_at: string
  hourly: Array<{ hour: number; visits: number }>
  daily: Array<{ day: string; visits: number }>
}
type State = { report: VisitorReport | null; status: 'loading' | 'ready' | 'unavailable' }
type Point = { label: string; detail: string; visits: number }
const shortDate = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
const fullDate = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
const hourLabel = (hour: number) => `${String(hour).padStart(2, '0')}:00`

function validReport(value: VisitorReport | null): value is VisitorReport {
  const count = (n: number) => Number.isSafeInteger(n) && n >= 0
  return Boolean(value && /^\d{4}-\d{2}-\d{2}$/.test(value.today)
    && Number.isFinite(Date.parse(value.started_at)) && Number.isFinite(Date.parse(value.checked_at))
    && Array.isArray(value.hourly) && value.hourly.length === 24
    && value.hourly.every((point, hour) => point.hour === hour && count(point.visits))
    && Array.isArray(value.daily) && value.daily.length === 30
    && value.daily.every(point => /^\d{4}-\d{2}-\d{2}$/.test(point.day) && Number.isFinite(Date.parse(point.day)) && count(point.visits)))
}

export function DashboardVisitorCharts({ active }: { active: boolean }) {
  const [state, setState] = React.useState<State>({ report: null, status: 'loading' })
  const [revision, retry] = React.useReducer(value => value + 1, 0)

  React.useEffect(() => {
    if (!active) return
    let disposed = false
    let interval: ReturnType<typeof setInterval> | undefined
    let request: { controller: AbortController; stale: boolean } | undefined
    let resumePending = false
    const visible = () => document.visibilityState === 'visible'
    const refresh = async () => {
      if (disposed || !visible() || request) return
      const current = { controller: new AbortController(), stale: false }
      request = current
      const timeout = setTimeout(() => current.controller.abort(), 10_000)
      const canUpdate = () => !disposed && !current.stale && visible()
      try {
        const { data, error } = await supabase.rpc('admin_dashboard_visitors').abortSignal(current.controller.signal)
        if (error || current.controller.signal.aborted || !validReport(data)) throw new Error('Visitor charts unavailable')
        if (canUpdate()) setState({ report: data, status: 'ready' })
      } catch {
        if (canUpdate()) setState({ report: null, status: 'unavailable' })
      } finally {
        clearTimeout(timeout)
        request = undefined
        if (resumePending) { resumePending = false; void refresh() }
      }
    }
    const sync = () => {
      clearInterval(interval)
      interval = undefined
      resumePending = false
      if (!visible()) {
        if (request) { request.stale = true; request.controller.abort() }
        return
      }
      setState({ report: null, status: 'loading' })
      if (request) resumePending = true
      else void refresh()
      interval = setInterval(() => void refresh(), 60_000)
    }
    document.addEventListener('visibilitychange', sync)
    sync()
    return () => {
      disposed = true
      resumePending = false
      clearInterval(interval)
      request?.controller.abort()
      document.removeEventListener('visibilitychange', sync)
    }
  }, [active, revision])

  const hourly = state.report?.hourly.map(point => ({
    label: hourLabel(point.hour), detail: `${hourLabel(point.hour)}–${hourLabel(point.hour + 1)}`, visits: point.visits,
  })) ?? []
  const daily = state.report?.daily.map(point => ({
    label: shortDate.format(new Date(`${point.day}T12:00:00Z`)),
    detail: fullDate.format(new Date(`${point.day}T12:00:00Z`)), visits: point.visits,
  })) ?? []
  return <>
    <VisitorChart title="Visitors today" points={hourly} status={state.status} onRetry={retry} />
    <VisitorChart title="Visitors last 30 days" points={daily} status={state.status} onRetry={retry} />
  </>
}

function VisitorChart({ title, points, status, onRetry }: {
  title: string; points: Point[]; status: State['status']; onRetry: () => void
}) {
  const id = React.useId()
  const empty = points.every(point => point.visits === 0)
  return <section className="dashboard-visitor-panel" aria-labelledby={id}>
    <header><h2 id={id}>{title}</h2></header>
    <div className="dashboard-visitor-chart">
      {status !== 'ready' ? <div className="dashboard-chart-status" role="status">
        <span>{status === 'loading' ? 'Loading visitors…' : 'Visitor data unavailable'}</span>
        {status === 'unavailable' && <Button type="button" className="secondary" onClick={onRetry}>Retry</Button>}
      </div> : <>
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <BarChart data={points} accessibilityLayer margin={{ top: 8, right: 0, bottom: 0, left: 0 }} barCategoryGap="24%">
            <CartesianGrid vertical={false} stroke="var(--admin-border)" strokeDasharray="3 3" />
            <XAxis dataKey="label" axisLine={false} tickLine={false} tick={{ fill: 'var(--admin-muted)', fontSize: 10 }} tickMargin={10} minTickGap={26} height={30} />
            <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={{ fill: 'var(--admin-muted)', fontSize: 10 }} width={32} tickMargin={8} domain={[0, 'auto']} tickCount={4} />
            <Tooltip cursor={{ fill: 'var(--admin-subtle)' }} content={({ active: hovered, payload }) => {
              const point = payload?.[0]?.payload as Point | undefined
              return hovered && point ? <div className="dashboard-chart-tooltip"><span>{point.detail}</span><strong>{point.visits.toLocaleString('en-GB')} booking visit{point.visits === 1 ? '' : 's'}</strong></div> : null
            }} />
            <Bar dataKey="visits" name="Booking visits" fill="var(--admin-accent)" fillOpacity={0.8} radius={[3, 3, 0, 0]} maxBarSize={20} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
        {empty && <span className="dashboard-chart-empty">No booking visits yet</span>}
      </>}
    </div>
  </section>
}
